'use client';

import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { ICardMediaAsset, ImageProviders, MediaAsset, MediaAssetType } from '@/types/media';
import {
  BlockStack,
  Box,
  Button,
  Card,
  Form,
  FormLayout,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStockMediaInfiniteQuery } from '../../../hooks/useStockMediaInfiniteQuery';
import { VIDEO_FRAGMENT_DURATION_MS } from '../../common/VideoAssetPreview';

export type StockVideosModalSectionProps = ImagesModalSectionProps;

const StockVideosModalSection = ({
  recommendation,
  setSelectedMedia,
  selectedMedia,
  multiSelection = true,
}: StockVideosModalSectionProps) => {
  const { stockMediaQuery } = recommendation || {};
  const recommendedQuery = stockMediaQuery || '';
  const [queryInput, setQueryInput] = useState(recommendedQuery);
  const [searchQuery, setSearchQuery] = useState(recommendedQuery);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (recommendedQuery && !hasInitialized.current) {
      setQueryInput(recommendedQuery);
      setSearchQuery(recommendedQuery);
      hasInitialized.current = true;
    }
  }, [recommendedQuery]);

  const { media, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } = useStockMediaInfiniteQuery({
    type: MediaAssetType.video,
    query: searchQuery,
  });

  const onVideoClick = useCallback(
    (asset: ICardMediaAsset) => {
      const isAlreadySelected = selectedMedia.some((media) => media.src === asset.src);

      if (isAlreadySelected) {
        setSelectedMedia((prev) => prev.filter((media) => media.src !== asset.src));
        return;
      }

      const newVideo: MediaAsset = {
        type: MediaAssetType.video,
        id: uuidv4(),
        alt: 'Stock video',
        provider: ImageProviders.stock,
        previewImage: asset.previewImage,
        src: asset.src!,
        settings: {
          playFrom: 0,
          playTill: VIDEO_FRAGMENT_DURATION_MS,
          duration: VIDEO_FRAGMENT_DURATION_MS,
        },
      };

      setSelectedMedia((prev) => {
        if (!multiSelection) {
          return [newVideo];
        }
        return [...prev, newVideo];
      });
    },
    [setSelectedMedia, selectedMedia, multiSelection]
  );

  const renderLoading = () => {
    return (
      <InlineStack align="center">
        <Spinner accessibilityLabel="Loading stock videos" size="large" />
      </InlineStack>
    );
  };

  const items = useMemo(
    () =>
      media.map((asset) => ({
        id: uuidv4(),
        src: asset.src,
        type: MediaAssetType.video,
        previewImage: asset.previewImage,
      })),
    [media]
  );

  const renderItem = useCallback(
    (asset) => {
      return (
        <MediaAssetCard
          size="small"
          hideUseAction
          aspectRatio="portrait"
          media={asset}
          key={asset.id}
          selected={selectedMedia.some((media) => media.src === asset.src)}
          onSelect={onVideoClick}
        />
      );
    },
    [onVideoClick, selectedMedia]
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage, hasNextPage]);

  const customScrollParentRef = useRef<HTMLDivElement>(null);

  return (
    <BlockStack gap="400">
      <Card>
        <FormLayout>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              setSearchQuery(queryInput);
            }}
          >
            <TextField
              label="Search stock videos"
              value={queryInput}
              onChange={setQueryInput}
              autoComplete="off"
              placeholder="Search stock videos..."
              connectedRight={<Button onClick={() => setSearchQuery(queryInput)}>Search</Button>}
            />
          </Form>
        </FormLayout>
      </Card>
      <div className="flex flex-1 flex-col" ref={customScrollParentRef}>
        <BlockStack gap="400">
          {media.length > 0 ? (
            <>
              <MediaGrid items={items} size="medium" renderItem={renderItem} />
              {hasNextPage && (
                <div className="flex justify-center py-4">
                  <Button onClick={handleLoadMore} disabled={isFetchingNextPage}>
                    Load more videos
                  </Button>
                </div>
              )}
            </>
          ) : (
            searchQuery &&
            !isLoading && (
              <div className="flex h-60 items-center justify-center">
                <Box background="bg-surface-secondary" padding="600" borderRadius="300">
                  <BlockStack gap="400" align="center">
                    <div className="flex justify-center">
                      <div className="bg-surface rounded-full p-4">
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M15.5 10.5L18.5 13.5L15.5 16.5M8.5 10.5L5.5 13.5L8.5 16.5M4.5 19.5H19.5C20.6046 19.5 21.5 18.6046 21.5 17.5V6.5C21.5 5.39543 20.6046 4.5 19.5 4.5H4.5C3.39543 4.5 2.5 5.39543 2.5 6.5V17.5C2.5 18.6046 3.39543 19.5 4.5 19.5Z"
                            stroke="#5C5F62"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                    <Text as="h2" variant="headingMd" alignment="center">
                      No videos found
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                      Try adjusting your search terms or use different keywords
                    </Text>
                    <Button onClick={() => setQueryInput('')}>Clear search</Button>
                  </BlockStack>
                </Box>
              </div>
            )
          )}
        </BlockStack>
        {(isLoading || isFetchingNextPage) && renderLoading()}
      </div>
    </BlockStack>
  );
};

export default memo(StockVideosModalSection);
